/**
 * AdminStats.tsx — admin overview dashboard.
 *
 * Single round-trip to GET /api/admin/dashboard/overview, light theme,
 * brand palette (orange + navy + neutral grays). The old version had:
 *   - hardcoded "Operational" status panel (lied about uptime)
 *   - "Monthly Revenue" was actually all-time revenue
 *   - quick-action buttons did nothing
 *   - no infrastructure signal at all
 * This one fixes all of that and adds 30d revenue, host health, alerts.
 *
 * Hard rule: no dark theme anywhere. The user has been explicit. Use the
 * gradient/glass effect on light backgrounds; bring color via brand
 * accent + status colours (green/amber/red).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../../config/environment';

interface DashboardData {
  kpis: {
    customers: { total: number; new_this_week: number; total_7d_ago: number };
    running_vps: { running: number; total: number; provisioned_this_week: number };
    hosts: { healthy: number; warning: number; critical: number; unreachable: number; total: number };
    mrr_usd: number;
  };
  revenue_30d: Array<{ day: string; completed_amount: number; completed_count: number; pending_count: number }>;
  alerts: Array<{ kind: string; severity: 'critical' | 'warning'; label: string; detail: string; link: string }>;
  ops: {
    pending_orders: number;
    pending_orders_amount: number;
    open_tickets: number;
    urgent_tickets: number;
    failed_payments_7d: number;
  };
  recent_activity: Array<{ kind: string; description: string; detail: string; at: string }>;
}

const fmtUSD = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtInt = (n: number) => n.toLocaleString('en-US');
const timeAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
};

/**
 * Hero KPI tile — large headline number, optional delta vs prior period.
 *
 * Tone semantics in the logo palette (navy + yellow + white):
 *   - 'brand-yellow' : positive accent (KPI is in the green; visual sunshine)
 *   - 'brand-navy'   : neutral / informational (most KPIs land here)
 *   - 'status-bad'   : critical condition — pale yellow tint with a
 *                      navy 🚨 badge, since red is not in our palette.
 * Status is conveyed via icon + tint intensity within the yellow family,
 * never via off-brand red/green.
 */
const KpiTile: React.FC<{
  label: string;
  value: string;
  delta?: { text: string; tone: 'up' | 'down' | 'neutral' };
  sub?: React.ReactNode;
  tone: 'brand-yellow' | 'brand-navy' | 'status-bad';
  icon: React.ReactNode;
}> = ({ label, value, delta, sub, tone, icon }) => {
  const accent = {
    'brand-yellow': 'from-secondary-300 to-secondary-500',
    'brand-navy':   'from-primary-700  to-primary-500',
    // "Bad" KPIs (critical hosts, failed payments, etc.) — deeper yellow
    // shades signal urgency without leaving the brand family. The 🚨 icon
    // does the heavy lifting for severity.
    'status-bad':   'from-secondary-700 to-secondary-500',
  } as const;
  const deltaColor = !delta ? '' :
    delta.tone === 'up'   ? 'text-secondary-700' :   // positive movement = brand yellow shade
    delta.tone === 'down' ? 'text-primary-navy' :    // negative movement = brand navy
                            'text-gray-500';

  return (
    <div className="relative bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Top accent ribbon — brand gradient only */}
      <div className={`h-1 bg-gradient-to-r ${accent[tone]}`} />
      <div className="p-5">
        <div className="flex items-start justify-between">
          <div>
            <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{label}</div>
            <div className="mt-2 text-3xl font-bold text-primary-navy tabular-nums">{value}</div>
          </div>
          <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${accent[tone]} text-white flex items-center justify-center shadow-md`}>
            {icon}
          </div>
        </div>
        <div className="mt-3 flex items-center gap-3 text-xs">
          {delta && <span className={`font-medium ${deltaColor}`}>{delta.text}</span>}
          {sub && <span className="text-gray-500">{sub}</span>}
        </div>
      </div>
    </div>
  );
};

/**
 * 30-day revenue bar chart. Bars are gradient-filled to match the brand
 * palette; pending count piggybacks the same x-axis as a lighter
 * sparkline-style overlay. Tooltip on hover shows the actual numbers.
 */
const RevenueChart: React.FC<{ series: DashboardData['revenue_30d'] }> = ({ series }) => {
  if (!series.length) return null;
  const max = Math.max(1, ...series.map(s => s.completed_amount));
  const W = 520;
  const H = 140;
  const step = W / series.length;

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Revenue — last 30 days</div>
          <div className="mt-1 text-2xl font-bold text-primary-navy tabular-nums">
            {fmtUSD(series.reduce((s, x) => s + x.completed_amount, 0))}
          </div>
        </div>
        <div className="text-xs text-gray-500">
          {series.reduce((s, x) => s + x.completed_count, 0)} completed · {series.reduce((s, x) => s + x.pending_count, 0)} pending
        </div>
      </div>
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H + 20}`} className="w-full h-44" preserveAspectRatio="none">
          <defs>
            {/* Bars: brand yellow gradient (light at top, brand yellow at
                bottom). Pending dots: brand navy at low opacity so they
                read as secondary signal but stay in the family. */}
            <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor="#fdc966" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#feb44b" stopOpacity="1" />
            </linearGradient>
          </defs>
          {/* Light horizontal gridlines */}
          {[0.25, 0.5, 0.75].map(f => (
            <line key={f} x1={0} y1={H * f} x2={W} y2={H * f} stroke="#f3f4f6" strokeWidth={1} />
          ))}
          {series.map((s, i) => {
            const x = i * step + 1;
            const barW = Math.max(2, step - 3);
            const h = (s.completed_amount / max) * H;
            const y = H - h;
            return (
              <g key={s.day}>
                <rect
                  x={x} y={y} width={barW} height={h || 1}
                  rx={2}
                  fill="url(#rev-grad)"
                >
                  <title>{`${s.day}: ${fmtUSD(s.completed_amount)} (${s.completed_count} orders, ${s.pending_count} pending)`}</title>
                </rect>
                {/* Pending count dots — brand navy, near baseline */}
                {s.pending_count > 0 && (
                  <circle cx={x + barW / 2} cy={H + 6} r={Math.min(3, 1 + s.pending_count / 4)} fill="#002448" fillOpacity={0.45}>
                    <title>{`${s.pending_count} pending on ${s.day}`}</title>
                  </circle>
                )}
              </g>
            );
          })}
        </svg>
        <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded bg-gradient-to-b from-secondary-300 to-secondary-500" /> completed
          </span>
          <span className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-primary-navy opacity-50" /> pending (count)
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Infrastructure card: shows fleet health derived from kpis.hosts. Click
 * navigates to the full host-density page. Color picks itself based on
 * whether any host is critical/warning.
 */
const InfraCard: React.FC<{ kpi: DashboardData['kpis']['hosts']; onNavigate: () => void }> = ({ kpi, onNavigate }) => {
  // Tone within the navy/yellow/white family:
  //   bad   → deeper yellow tint + 🚨 icon (matches a critical state without red)
  //   watch → pale yellow tint + ⚠ icon
  //   good  → white tint + ✓ icon
  // The icon carries severity; colour just amplifies. No off-brand reds.
  const tone = kpi.critical > 0 ? 'bad' : kpi.warning > 0 || kpi.unreachable > 0 ? 'watch' : 'good';
  const toneStyles = {
    bad:   { bg: 'from-secondary-100 to-secondary-200', tag: 'bg-secondary-500 text-primary-navy ring-secondary-600', icon: '🚨', label: 'Action needed' },
    watch: { bg: 'from-secondary-50  to-secondary-100', tag: 'bg-secondary-300 text-primary-navy ring-secondary-400', icon: '⚠️',  label: 'Plan capacity' },
    good:  { bg: 'from-white         to-secondary-50',  tag: 'bg-white text-primary-navy ring-primary-100',           icon: '✓',  label: 'All healthy' },
  } as const;
  const s = toneStyles[tone];

  // Per-bucket dots stay within the navy/yellow family too. Density of
  // the swatch implies severity:
  //   critical → solid navy
  //   warning  → brand yellow
  //   healthy  → light yellow tint
  //   offline  → mid gray (a structural neutral, not a hue)
  const bucketDots: Record<'healthy' | 'warning' | 'critical' | 'offline', string> = {
    healthy:  'bg-secondary-300',
    warning:  'bg-secondary-500',
    critical: 'bg-primary-navy',
    offline:  'bg-gray-400',
  };

  return (
    <div className={`bg-gradient-to-br ${s.bg} rounded-2xl border border-white shadow-sm p-5`}>
      <div className="flex items-baseline justify-between mb-4">
        <div>
          <div className="text-xs font-medium uppercase tracking-wider text-primary-navy/70">Infrastructure</div>
          <div className="mt-1 text-2xl font-bold text-primary-navy tabular-nums">
            {kpi.healthy}/{kpi.total} healthy
          </div>
        </div>
        <span className={`text-xs px-2 py-1 rounded-full ring-1 font-medium ${s.tag} inline-flex items-center gap-1`}>
          <span aria-hidden>{s.icon}</span>{s.label}
        </span>
      </div>

      <div className="grid grid-cols-4 gap-2 mb-4">
        {[
          { label: 'Healthy',  count: kpi.healthy,     dot: bucketDots.healthy },
          { label: 'Warning',  count: kpi.warning,     dot: bucketDots.warning },
          { label: 'Critical', count: kpi.critical,    dot: bucketDots.critical },
          { label: 'Offline',  count: kpi.unreachable, dot: bucketDots.offline },
        ].map(c => (
          <div key={c.label} className="bg-white/80 rounded-lg p-2 text-center">
            <div className="flex items-center justify-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${c.dot}`} />
              <span className="text-xs text-primary-navy/70">{c.label}</span>
            </div>
            <div className="text-xl font-bold text-primary-navy mt-1">{c.count}</div>
          </div>
        ))}
      </div>

      <button
        onClick={onNavigate}
        className="w-full text-sm font-medium text-primary-navy hover:text-primary-navy bg-white/80 hover:bg-white border border-primary-100 rounded-lg py-2 transition"
      >
        Open Host Density →
      </button>
    </div>
  );
};

const AlertBanner: React.FC<{ alerts: DashboardData['alerts']; onLink: (path: string) => void }> = ({ alerts, onLink }) => {
  if (!alerts.length) return null;
  return (
    <div className="space-y-2">
      {alerts.map((a, i) => {
        const isCritical = a.severity === 'critical';
        // Critical: brand yellow background with a navy text + 🚨 icon — the
        // strongest in-brand expression of urgency. Warning: pale yellow.
        // We do not use rose/amber because they're outside the logo family.
        return (
          <button
            key={i}
            onClick={() => onLink(a.link)}
            className={`w-full text-left rounded-xl border p-4 transition shadow-sm flex items-center gap-4 ${
              isCritical
                ? 'bg-secondary-200 border-secondary-500 hover:bg-secondary-300'
                : 'bg-secondary-50 border-secondary-200 hover:bg-secondary-100'
            }`}
          >
            <span className="text-2xl">{isCritical ? '🚨' : '⚠️'}</span>
            <div className="flex-1">
              <div className="font-semibold text-primary-navy">{a.label}</div>
              <div className="text-sm text-primary-navy/80">{a.detail}</div>
            </div>
            <span className="text-primary-navy/40">→</span>
          </button>
        );
      })}
    </div>
  );
};

const QuickActions: React.FC<{ onNavigate: (path: string) => void }> = ({ onNavigate }) => {
  const actions = [
    { label: 'Users',           path: '/admin/users',         icon: '👥', desc: 'Manage customers + admins' },
    { label: 'Orders',          path: '/admin/orders',        icon: '🛒', desc: 'View + reconcile orders' },
    { label: 'Host Density',    path: '/admin/host-density',  icon: '📈', desc: 'Capacity + alerts' },
    { label: 'Support',         path: '/admin/support',       icon: '🎫', desc: 'Open + urgent tickets' },
  ];
  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500 mb-3">Quick Actions</div>
      <div className="grid grid-cols-2 gap-2">
        {actions.map(a => (
          <button
            key={a.path}
            onClick={() => onNavigate(a.path)}
            className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-secondary-500 hover:bg-secondary-50 transition text-left"
          >
            <span className="text-xl">{a.icon}</span>
            <div className="min-w-0">
              <div className="font-medium text-primary-navy text-sm">{a.label}</div>
              <div className="text-xs text-gray-500 truncate">{a.desc}</div>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

const ActivityFeed: React.FC<{ items: DashboardData['recent_activity'] }> = ({ items }) => (
  <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
    <div className="flex items-center justify-between mb-3">
      <div className="text-xs font-medium uppercase tracking-wider text-gray-500">Recent Activity</div>
      <span className="text-xs text-gray-400">{items.length} events</span>
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-gray-500 py-6 text-center">No activity yet</div>
    ) : (
      <ol className="space-y-3">
        {items.map((it, i) => {
          // Two activity kinds, two in-brand dots: customer = navy,
          // order = yellow. Lets you tell them apart without leaving
          // the logo palette.
          const dot = it.kind === 'customer' ? 'bg-primary-navy' : 'bg-secondary-500';
          return (
            <li key={i} className="flex items-start gap-3">
              <span className={`mt-1.5 w-2 h-2 rounded-full flex-none ${dot}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-primary-navy truncate">{it.description}</div>
                <div className="text-xs text-gray-500">{it.detail} · {timeAgo(it.at)}</div>
              </div>
            </li>
          );
        })}
      </ol>
    )}
  </div>
);

const OpsStrip: React.FC<{ ops: DashboardData['ops']; onLink: (p: string) => void }> = ({ ops, onLink }) => {
  const items = [
    { label: 'Pending orders',       value: fmtInt(ops.pending_orders),       sub: fmtUSD(ops.pending_orders_amount), link: '/admin/orders',  tone: ops.pending_orders >= 50 ? 'watch' : 'calm' },
    { label: 'Open tickets',         value: fmtInt(ops.open_tickets),         sub: `${ops.urgent_tickets} urgent`,    link: '/admin/support', tone: ops.urgent_tickets > 0 ? 'bad' : 'calm' },
    { label: 'Failed payments (7d)', value: fmtInt(ops.failed_payments_7d),   sub: 'review + retry',                  link: '/admin/orders',  tone: ops.failed_payments_7d > 0 ? 'bad' : 'calm' },
  ];
  // Tones map to logo-family intensities. We deliberately leave the
  // baseline tone navy (the strong brand colour) so the card still feels
  // grounded when nothing is wrong.
  const toneClass = (t: string) =>
    t === 'bad'   ? 'text-primary-navy font-extrabold' :
    t === 'watch' ? 'text-secondary-700' :
                    'text-primary-navy';
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
      {items.map(it => (
        <button
          key={it.label}
          onClick={() => onLink(it.link)}
          className="bg-white rounded-xl border border-gray-200 shadow-sm p-4 text-left hover:border-secondary-500 transition"
        >
          <div className="text-xs font-medium uppercase tracking-wider text-gray-500">{it.label}</div>
          <div className={`mt-1 text-2xl tabular-nums ${toneClass(it.tone)}`}>{it.value}</div>
          <div className="text-xs text-gray-500 mt-1">{it.sub}</div>
        </button>
      ))}
    </div>
  );
};

const AdminStats: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = await fetch(getApiUrl('/admin/dashboard/overview'), {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const j = await r.json();
      if (!j.success) { setError(j.error || 'Failed to load dashboard'); return; }
      setData(j.data as DashboardData);
      setError(null);
    } catch (e: any) {
      setError(e?.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.role !== 'admin') { setLoading(false); return; }
    load();
    // Refresh every 60s — most dashboard widgets are slow-moving except
    // host health, and we don't want to thrash Proxmox.
    const t = setInterval(load, 60_000);
    return () => clearInterval(t);
  }, [user, load]);

  // Support-role dashboard intentionally not modernised here — that's a
  // separate component-worth of work. Keep the prior simple support panel
  // (lives in this file historically). We just delegate the rendering.
  if (user?.role === 'support') {
    return <SupportSimpleDashboard navigate={navigate} />;
  }

  if (loading && !data) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <div key={i} className="bg-white rounded-2xl border border-gray-200 p-5 animate-pulse h-32">
            <div className="h-3 w-1/3 bg-gray-200 rounded mb-3" />
            <div className="h-8 w-2/3 bg-gray-200 rounded mb-3" />
            <div className="h-3 w-1/2 bg-gray-200 rounded" />
          </div>
        ))}
      </div>
    );
  }
  if (error) {
    return (
      <div className="bg-secondary-50 border border-secondary-300 rounded-xl p-4 text-primary-navy">
        <strong>Dashboard error:</strong> {error}
      </div>
    );
  }
  if (!data) return null;

  const k = data.kpis;
  const customerDelta = k.customers.total - k.customers.total_7d_ago;

  return (
    <div className="space-y-6">
      {/* Page header — brand gradient: navy on the left fading into the
          logo yellow on the right. This is the strongest "brand splash"
          surface in the app. */}
      <div className="rounded-2xl bg-gradient-to-r from-primary-navy via-primary-700 to-secondary-500 p-6 text-white shadow-lg">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h2 className="text-2xl font-bold">Platform Overview</h2>
            <p className="text-sm text-white/90 mt-1">
              Live operational view · refreshes every 60 seconds
            </p>
          </div>
          <div className="flex items-center gap-2 bg-white/15 backdrop-blur rounded-lg px-3 py-1.5 text-sm">
            <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
            Live
          </div>
        </div>
      </div>

      {/* KPI hero row */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiTile
          label="MRR"
          value={fmtUSD(k.mrr_usd)}
          sub={<>active subs ·{' '}<span className="text-primary-navy font-medium">Stripe</span></>}
          tone="brand-yellow"
          icon={<span>$</span>}
        />
        <KpiTile
          label="Customers"
          value={fmtInt(k.customers.total)}
          delta={{
            text: customerDelta > 0 ? `+${customerDelta} this week` : 'no change this week',
            tone: customerDelta > 0 ? 'up' : 'neutral',
          }}
          tone="brand-navy"
          icon={<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6 5.87a4 4 0 10-8 0M21 7a4 4 0 11-8 0 4 4 0 018 0z" /></svg>}
        />
        <KpiTile
          label="Running VPS"
          value={`${k.running_vps.running} / ${k.running_vps.total}`}
          delta={{
            text: k.running_vps.provisioned_this_week > 0 ? `+${k.running_vps.provisioned_this_week} new` : 'no new this week',
            tone: k.running_vps.provisioned_this_week > 0 ? 'up' : 'neutral',
          }}
          tone="brand-navy"
          icon={<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M5 12a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v4a2 2 0 01-2 2M5 12a2 2 0 00-2 2v4a2 2 0 002 2h14a2 2 0 002-2v-4a2 2 0 00-2-2" /></svg>}
        />
        <KpiTile
          label="Hosts Healthy"
          value={`${k.hosts.healthy} / ${k.hosts.total}`}
          delta={
            k.hosts.critical > 0
              ? { text: `${k.hosts.critical} critical`, tone: 'down' }
              : k.hosts.warning > 0
                ? { text: `${k.hosts.warning} warning`, tone: 'neutral' }
                : { text: 'all healthy', tone: 'up' }
          }
          // Critical hosts pick up the 'status-bad' deeper-yellow ribbon;
          // otherwise the tile is yellow (everything ok) or navy (warning).
          tone={k.hosts.critical > 0 ? 'status-bad' : k.hosts.warning > 0 ? 'brand-navy' : 'brand-yellow'}
          icon={<svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
        />
      </div>

      {/* Alerts (collapses cleanly when empty) */}
      <AlertBanner alerts={data.alerts} onLink={(p) => navigate(p)} />

      {/* Two-up: chart + infra */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><RevenueChart series={data.revenue_30d} /></div>
        <InfraCard kpi={data.kpis.hosts} onNavigate={() => navigate('/admin/host-density')} />
      </div>

      {/* Ops counters */}
      <OpsStrip ops={data.ops} onLink={(p) => navigate(p)} />

      {/* Activity + Quick actions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2"><ActivityFeed items={data.recent_activity} /></div>
        <QuickActions onNavigate={(p) => navigate(p)} />
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Support-role simple dashboard — preserved from the previous version, */
/* trimmed to remove dead code. Same look as before so support staff   */
/* don't have a relearning curve.                                      */
/* ------------------------------------------------------------------ */
const SupportSimpleDashboard: React.FC<{ navigate: (p: string) => void }> = ({ navigate }) => {
  const [stats, setStats] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(getApiUrl('/admin/support/statistics'), {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        const j = await r.json();
        if (j.success) setStats(j.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <div className="grid grid-cols-1 md:grid-cols-5 gap-4">{[...Array(5)].map((_, i) => <div key={i} className="h-24 bg-white border rounded-xl animate-pulse" />)}</div>;
  if (!stats)  return <div className="text-gray-500">Unable to load support statistics</div>;

  const cards = [
    { label: 'Total Tickets', value: stats.total,       icon: '🎫' },
    { label: 'Open',          value: stats.open,        icon: '🆕' },
    { label: 'In Progress',   value: stats.in_progress, icon: '⏳' },
    { label: 'Urgent',        value: stats.urgent,      icon: '🚨' },
    { label: 'Unassigned',    value: stats.unassigned,  icon: '📋' },
  ];
  return (
    <div className="space-y-6">
      {/* Same navy → yellow brand gradient as the admin overview hero,
          for visual continuity across the two role views. */}
      <div className="rounded-2xl bg-gradient-to-r from-primary-navy via-primary-700 to-secondary-500 p-6 text-white shadow">
        <h2 className="text-2xl font-bold">Support Dashboard</h2>
        <p className="text-sm text-white/90 mt-1">Customer tickets &amp; quick triage</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        {cards.map(c => (
          <div key={c.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-2">
              <span className="text-lg">{c.icon}</span>
              <div className="text-xs font-medium uppercase text-gray-500">{c.label}</div>
            </div>
            <div className="mt-2 text-2xl font-bold text-primary-navy">{c.value}</div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-2xl border border-gray-200 p-5">
        <h3 className="text-sm font-medium uppercase text-gray-500 mb-3">Quick Actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <button onClick={() => navigate('/admin/support')} className="p-3 border border-gray-200 rounded-lg hover:border-secondary-500 hover:bg-secondary-50 text-left flex items-center gap-3">
            <span className="text-xl">🎫</span><div><div className="font-medium text-sm text-primary-navy">All tickets</div><div className="text-xs text-gray-500">Browse queue</div></div>
          </button>
          <button onClick={() => navigate('/admin/support?status=open')} className="p-3 border border-gray-200 rounded-lg hover:border-secondary-500 hover:bg-secondary-50 text-left flex items-center gap-3">
            <span className="text-xl">🆕</span><div><div className="font-medium text-sm text-primary-navy">Open tickets</div><div className="text-xs text-gray-500">Pick one to work</div></div>
          </button>
          <button onClick={() => navigate('/admin/support?priority=urgent')} className="p-3 border border-gray-200 rounded-lg hover:border-secondary-500 hover:bg-secondary-50 text-left flex items-center gap-3">
            <span className="text-xl">🚨</span><div><div className="font-medium text-sm text-primary-navy">Urgent</div><div className="text-xs text-gray-500">Top priority</div></div>
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminStats;
